import React, { FC, ReactNode, createContext, useContext, useState } from 'react';
import { useFirebaseAuth } from './FirebaseAuthContext';
import PaymentMethods from '../components/PaymentMethods';

interface Investment {
  id: number;
  projectId: number;
  projectName: string;
  solAmount: number;
  usdAmount: number;
  investmentDate: string;
  userEmail: string;
}

interface PublicKey {
  toBase58(): string;
}

interface WalletContextState {
  connected: boolean;
  publicKey: PublicKey | null;
  balance: number;
  connect: () => Promise<void>;
  disconnect: () => void;
  signTransaction: (transaction: any) => Promise<any>;
  isAuthRequired: boolean;
  updateBalance: (newBalance: number) => void;
  getUserInvestments: (email: string) => Investment[];
  addInvestment: (investment: Omit<Investment, 'id' | 'investmentDate'>) => void;
}

const WalletContext = createContext<WalletContextState>({
  connected: false,
  publicKey: null,
  balance: 0,
  connect: async () => { },
  disconnect: () => { },
  signTransaction: async (transaction: any) => transaction,
  isAuthRequired: true,
  updateBalance: () => { },
  getUserInvestments: () => [],
  addInvestment: () => { },
});

interface SimpleWalletProviderProps {
  children: ReactNode;
}

export const SimpleWalletProvider: FC<SimpleWalletProviderProps> = ({ children }) => {
  const [connected, setConnected] = useState(false);
  const [publicKey, setPublicKey] = useState<PublicKey | null>(null);
  const [balance, setBalance] = useState<number>(0);
  const { user } = useFirebaseAuth();

  // Get investments for a specific user email
  const getUserInvestments = (email: string): Investment[] => {
    const stored = localStorage.getItem('userInvestments');
    if (!stored) return [];

    try {
      const allInvestments: Investment[] = JSON.parse(stored);
      return allInvestments.filter(inv => inv.userEmail === email);
    } catch (error) {
      console.error('Error parsing investments:', error);
      return [];
    }
  };

  // Add a new investment
  const addInvestment = (investment: Omit<Investment, 'id' | 'investmentDate'>) => {
    const stored = localStorage.getItem('userInvestments');
    let allInvestments: Investment[] = [];

    if (stored) {
      try {
        allInvestments = JSON.parse(stored);
      } catch (error) {
        console.error('Error parsing existing investments:', error);
      }
    }

    const newInvestment: Investment = {
      ...investment,
      id: Date.now() + Math.random(), // Simple ID generation
      investmentDate: new Date().toISOString()
    };

    allInvestments.push(newInvestment);
    localStorage.setItem('userInvestments', JSON.stringify(allInvestments));
  };

  const connect = async () => {
    // Check if user is authenticated before allowing wallet connection
    if (!user) {
      throw new Error('Please log in to connect your wallet');
    }

    // Simulate wallet connection with user-specific data
    setConnected(true);
    // Generate a more realistic looking Solana address format
    const mockAddress = generateMockSolanaAddress(user.id);
    setPublicKey({
      toBase58: () => mockAddress
    });

    // Get user's investment history and calculate remaining balance
    const userInvestments = getUserInvestments(user.email);
    const totalInvested = userInvestments.reduce((sum, inv) => sum + inv.solAmount, 0);
    const initialBalance = generateDemoBalance(user.id);
    const remainingBalance = Math.max(0, initialBalance - totalInvested);

    setBalance(remainingBalance);
  };

  // Generate a mock Solana address that looks realistic and is consistent per user
  const generateMockSolanaAddress = (userId: string | number): string => {
    // Create a deterministic but realistic-looking Solana address
    const characters = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz123456789';
    let address = '';

    // Use user ID as seed for consistent address per user
    // Convert string ID to number for seeding
    const numericSeed = typeof userId === 'string' ?
      userId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) :
      userId;
    let seed = numericSeed * 12345;

    for (let i = 0; i < 44; i++) {
      seed = (seed * 9301 + 49297) % 233280; // Linear congruential generator
      address += characters[seed % characters.length];
    }

    return address;
  };

  const disconnect = () => {
    setConnected(false);
    setPublicKey(null);
    setBalance(0);
  };

  // Generate a demo balance based on user ID for consistency
  const generateDemoBalance = (userId: string | number): number => {
    // Set demo balance to 500 SOL for all users
    return 500.0;
  };

  const updateBalance = (newBalance: number) => {
    setBalance(newBalance);
  };

  const signTransaction = async (transaction: any) => {
    // Simulate transaction signing
    return { ...transaction, signed: true };
  };

  return (
    <WalletContext.Provider value={{
      connected,
      publicKey,
      balance,
      connect,
      disconnect,
      signTransaction,
      isAuthRequired: !user,
      updateBalance,
      getUserInvestments,
      addInvestment,
    }}>
      {children}
    </WalletContext.Provider>
  );
};

export const useWallet = () => {
  return useContext(WalletContext);
};

// Simple wallet button component
export const WalletMultiButton: FC<{ className?: string }> = ({ className = '' }) => {
  const { connected, connect, disconnect, publicKey, balance } = useWallet();
  const [showPaymentMethods, setShowPaymentMethods] = useState(false);

  const handleClick = () => {
    if (connected) {
      disconnect();
    } else {
      connect();
    }
  };

  const handleAddMoney = () => {
    setShowPaymentMethods(true);
  };

  return (
    <>
      <div className="flex items-center gap-3">
        <button
          onClick={handleClick}
          className={`px-3 py-1.5 rounded-md font-medium text-sm ${connected
            ? 'bg-green-600 hover:bg-green-700 text-white'
            : 'bg-blue-600 hover:bg-blue-700 text-white'
            } ${className}`}
        >
          {connected ? `Disconnect (${publicKey?.toBase58().slice(0, 6)}...)` : 'Connect Wallet'}
        </button>
        {connected && (
          <>
            <div className="text-center">
              <div className="text-green-400 font-bold text-sm">{balance} SOL</div>
              <div className="text-blue-200 text-xs">Wallet Balance</div>
            </div>
            <button
              onClick={handleAddMoney}
              className="px-3 py-1.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-md font-medium text-sm transition-all duration-200 hover:scale-105"
            >
              Add Money
            </button>
          </>
        )}
      </div>

      {/* Payment Methods Modal */}
      {showPaymentMethods && (
        <PaymentMethods
          isOpen={showPaymentMethods}
          onClose={() => setShowPaymentMethods(false)}
          onPaymentMethodSelect={(method) => {
            console.log('Selected payment method:', method);
            // Payment processing will be handled in the PaymentMethods component
          }}
        />
      )}
    </>
  );
};
